import React, { useState } from 'react';
import { canAccessDebugPanel, isSuperAdmin, USER_ROLES } from '../utils/userRoles';
import './AdminDebugTrigger.css';

const AdminDebugTrigger = ({ onToggle, isOpen, user, profile }) => {
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const userRole = profile?.user_role;
  const hasRoleAccess = canAccessDebugPanel(userRole);
  const isUserSuperAdmin = isSuperAdmin(userRole);

  // Don't render the trigger if user has no access at all
  if (!hasRoleAccess && !showPasswordPrompt) {
    return null;
  }

  const handleClick = () => {
    if (hasRoleAccess) {
      // User has role-based access, open directly
      onToggle();
      if (window.adminDebug) {
        window.adminDebug.addLog(`Admin debug panel ${!isOpen ? 'opened' : 'closed'} by ${userRole}`, 'system', {
          user_id: user?.sub,
          user_role: userRole,
          access_method: 'role_based'
        });
      }
    } else {
      // Show password prompt for emergency super admin access
      setShowPasswordPrompt(true);
      setPasswordError('');
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const correctPassword = process.env.REACT_APP_DEBUG_PANEL_PASSWORD || 'xjgtv^wBY4I3';
    
    if (password === correctPassword) {
      setShowPasswordPrompt(false);
      setPassword('');
      setPasswordError('');
      
      // Open the debug panel
      onToggle();
      
      if (window.adminDebug) {
        window.adminDebug.addLog('Admin debug panel accessed via emergency password', 'system', {
          user_id: user?.sub,
          user_role: userRole,
          access_method: 'emergency_password',
          timestamp: new Date().toISOString()
        });
      }
    } else {
      setPasswordError('Incorrect password');
      setPassword('');
      
      if (window.adminDebug) {
        window.adminDebug.addLog('Debug panel emergency password failed', 'warning', {
          user_id: user?.sub,
          user_role: userRole,
          timestamp: new Date().toISOString()
        });
      }
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordPrompt(false);
    setPassword('');
    setPasswordError('');
  };

  const getTriggerIcon = () => {
    if (isUserSuperAdmin) return '👑'; // Crown for super admin
    if (hasRoleAccess) return '🛠️';    // Tools for admin
    return '🔒';                       // Lock for password access
  };

  const getTriggerTitle = () => {
    if (isUserSuperAdmin) return 'Super Admin Debug Panel';
    if (hasRoleAccess) return 'Admin Debug Panel';
    return 'Emergency Admin Access (Password Required)';
  };

  return (
    <>
      <div className="admin-debug-trigger">
        <button
          onClick={handleClick}
          className={`debug-trigger-btn ${isOpen ? 'active' : ''} ${hasRoleAccess ? 'authorized' : 'locked'} ${isUserSuperAdmin ? 'super-admin' : ''}`}
          title={getTriggerTitle()}
        >
          {getTriggerIcon()}
        </button>
        <div className="debug-trigger-tooltip">
          {getTriggerTitle()}
        </div>
      </div>

      {showPasswordPrompt && (
        <div className="password-overlay">
          <div className="password-modal">
            <div className="password-header">
              <h3>🔐 Emergency Admin Access</h3>
              <p>Enter emergency password for debug panel access</p>
              <small>Note: You don't have admin role permissions</small>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="password-field">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter emergency password"
                  className={`password-input ${passwordError ? 'error' : ''}`}
                  autoFocus
                />
                {passwordError && (
                  <div className="password-error">{passwordError}</div>
                )}
              </div>
              
              <div className="password-actions">
                <button type="button" onClick={handlePasswordCancel} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Access Panel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDebugTrigger; 